import {
  capitalizeTypedWord,
  EXERCISE_COPY,
  exerciseKindSchema,
  nextStep,
  pickForMe,
} from '@echo/core';
import { useHasLibrary, useLibrary } from '@echo/core/react';
import { AnswerOption, Button, TextField } from '@echo/ui-web';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ContentFrame, ExercisePage } from '@/components/exercise-frame';
import { MathChoose } from '@/components/math-choose';
import { PaperCard } from '@/components/paper-card';
import type { ExerciseChoice } from '@/lib/exercise-routes';

export const Route = createFileRoute('/_app/exercise/$kind/choose')({
  params: {
    parse: ({ kind }) => ({ kind: exerciseKindSchema.parse(kind) }),
    stringify: ({ kind }) => ({ kind }),
  },
  component: ExerciseChoosePage,
});

/**
 * "What would you like to read/write?"; math has its own over the Math library
 * (`MathChoose`). "Pick for me" takes a Library book not read yet, at random (the workbook's next
 * passage where there is no library), or the workbook's next prompt; the other options are the
 * person's own material — a book or something else to read, or a kind of writing. Either way the choice goes on to the coach.
 * Reading's "Choose from Library" opens the Library's Reading shelf alone instead, where
 * choosing a book goes on to the coach.
 */
function ExerciseChoosePage() {
  const { kind } = Route.useParams();
  return kind === 'math' ? <MathChoose /> : <ReadWriteChoose kind={kind} />;
}

function ReadWriteChoose({ kind }: { kind: 'reading' | 'writing' }) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const copy = EXERCISE_COPY[kind];
  const hasLibrary = useHasLibrary();
  const library = useLibrary();
  const [selected, setSelected] = useState(0);
  const [title, setTitle] = useState('');
  // Option 0 is "pick for me" (the workbook); 1 the person's own book; then something else;
  // "Choose from Library" last, where the environment has a library.
  const options = (copy.choose?.options ?? []).filter((option) => !option.library || hasLibrary);
  const toLibrary = options[selected]?.library === true;
  const ownMaterial = kind === 'reading' && selected > 0 && !toLibrary;
  // "Pick for me" picks from the library, so it waits for it; the workbook only stands in
  // where there is no library or it could not be read.
  const picking = kind === 'reading' && selected === 0 && !library.ready;

  if (!copy.choose) {
    return <Navigate to="/exercise/$kind" params={{ kind }} replace />;
  }

  const proceed = () => {
    if (toLibrary) {
      void navigate({ to: '/library', search: { from: 'reading' } });
      return;
    }
    const typed = title.trim();
    const picked = kind === 'reading' && selected === 0 ? pickForMe(library.reading) : undefined;
    const choice: ExerciseChoice =
      kind === 'writing'
        ? selected === 0
          ? { source: 'workbook' }
          : { source: 'own', choice: String(selected) }
        : {
            source: selected === 0 ? 'workbook' : selected === 1 ? 'own' : 'other',
            ...(ownMaterial && typed ? { title: typed } : {}),
            ...(picked ? { passage: picked.lineageUuid } : {}),
          };
    if (nextStep(kind, 'choose') === 'intro') {
      void navigate({ to: '/exercise/$kind/intro', params: { kind }, state: { exercise: choice } });
    } else {
      void navigate({ to: '/exercise/$kind', params: { kind }, state: { exercise: choice } });
    }
  };

  return (
    <ExercisePage>
      <ContentFrame
        footer={
          <Button onClick={proceed} loading={picking}>
            {t`Continue`}
          </Button>
        }
      >
        <h1 className="w-full text-headline-lg text-exercise-prompt">{t(copy.choose.title)}</h1>
        <div className="flex w-full flex-col gap-content-sm">
          {options.map((option, index) => (
            <AnswerOption
              key={option.label.id ?? index}
              selected={index === selected}
              onClick={() => setSelected(index)}
            >
              {t(option.label)}
            </AnswerOption>
          ))}
        </div>
        {ownMaterial ? (
          <PaperCard>
            <div className="flex flex-col gap-content-sm">
              <p className="text-body-lg">
                {selected === 1
                  ? t`What is the title of the book you'll read?`
                  : t`What is it you'll be reading?`}
              </p>
              {/* The title is optional — Continue never waits for it. */}
              <TextField
                label={selected === 1 ? t`Book title` : t`Title`}
                indicator={t`(optional)`}
                hint={selected === 1 ? undefined : t`For example, newspaper, magazine etc.`}
                value={title}
                onChange={(event) => {
                  const input = event.currentTarget;
                  // Title case as typed, like the app; never mid-composition (Chinese,
                  // Japanese, Korean input methods type Latin letters on the way).
                  const native = event.nativeEvent as InputEvent;
                  const next = native.isComposing
                    ? input.value
                    : capitalizeTypedWord(title, input.value);
                  if (next !== input.value) {
                    const caret = input.selectionStart;
                    input.value = next;
                    input.setSelectionRange(caret, caret);
                  }
                  setTitle(next);
                }}
                autoCapitalize="words"
                autoComplete="off"
              />
            </div>
          </PaperCard>
        ) : null}
      </ContentFrame>
    </ExercisePage>
  );
}
